/**
 * AI 智能分类 · 任务列表 localStorage 持久化
 *
 * 用途：刷新页面 / 重开标签页能看到归档进度（用户在 30 分钟会议后回来还能确认
 * 之前建的相册有没有归完）。
 *
 * 策略
 *   · key: ai_classify_tasks_v1（带版本号便于将来 schema 变更）
 *   · 启动时一次性 read → dispatch tasksHydrated 灌进 store
 *   · 任意 task action 后 throttle 写一次（throttle 而非 debounce 避免丢最后一笔）
 *   · 仅持久化 tasks + hasUnreadCompletion；不存 polling 内部计时器
 *
 * 兼容：解析失败 / 旧版本 schema → silently fallback 空状态（不抛错卡死）
 */

import type { ArchiveTask } from './taskSlice'

const STORAGE_KEY = 'ai_classify_tasks_v1'
const SCHEMA_VERSION = 1

interface PersistedShape {
  v: number
  tasks: ArchiveTask[]
  hasUnreadCompletion: boolean
  /** 待弹 toast 通知的 taskId 队列 · v4.6 加入；旧 schema 没这字段，hydrate 兜底 [] */
  pendingNotifications?: string[]
}

/**
 * 启动时调一次 · 没数据 / 解析失败统一返回 null（调用方走默认值）
 */
export function loadTasksFromStorage(): {
  tasks: ArchiveTask[]
  hasUnreadCompletion: boolean
  pendingNotifications: string[]
} | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedShape
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      parsed.v !== SCHEMA_VERSION ||
      !Array.isArray(parsed.tasks)
    ) {
      return null
    }
    // 进入页面时把"还在 archiving 但创建超过阈值"的强制置 timeout，
    // 避免无限轮询（轮询逻辑里也会检查，这里是双保险）
    //
    // ⚠️ 跟 useArchiveTaskPolling.ts:TIMEOUT_MS 必须保持一致 — 之前两个值打架
    // (这里 10min / polling 3min) 让用户看到"刚开 2 分钟就超时但 UI 写 10 分钟"的
    // 错乱状态。统一为 3 分钟跟 polling 对齐。
    const TIMEOUT_MS = 3 * 60 * 1000
    const now = Date.now()
    const tasks = parsed.tasks.map((t) => {
      if (t.status === 'archiving' && now - t.createdAt > TIMEOUT_MS) {
        return { ...t, status: 'timeout' as const, completedAt: now }
      }
      return t
    })
    return {
      tasks,
      hasUnreadCompletion: !!parsed.hasUnreadCompletion,
      // 旧版本 schema 没这字段 · 兜底 []，避免 undefined 入 store
      pendingNotifications: Array.isArray(parsed.pendingNotifications)
        ? parsed.pendingNotifications
        : []
    }
  } catch {
    return null
  }
}

/**
 * 写入 · 失败 silently 吞（localStorage 满 / 无权限 / 隐身模式都可能失败）
 */
export function saveTasksToStorage(payload: {
  tasks: ArchiveTask[]
  hasUnreadCompletion: boolean
  pendingNotifications: string[]
}): void {
  if (typeof window === 'undefined') return
  try {
    const data: PersistedShape = {
      v: SCHEMA_VERSION,
      tasks: payload.tasks,
      hasUnreadCompletion: payload.hasUnreadCompletion,
      pendingNotifications: payload.pendingNotifications
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    /* silent: localStorage 写入失败不影响主流程 */
  }
}

/** 调试用：清空持久化（控制台敲 `localStorage.removeItem('ai_classify_tasks_v1')`） */
export function clearTasksFromStorage(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* silent */
  }
}
